/**
 * City-specific salary data for hospitality staffing.
 *
 * Source: aggregated 2025/2026 market data for luxury / 5-star establishments.
 * Ranges are stored as **annual** compensation in the city's **native currency**
 * (no conversion). The UI divides by 12 to render a monthly equivalent.
 *
 * Roles are grouped into departments so the UI can render collapsible sections.
 */

export type StaffDepartment =
  | "kitchen"
  | "floor"
  | "bar"
  | "sommelier"
  | "operations"
  | "admin"
  | "management"
  | "frontDesk"
  | "housekeeping"
  | "kitchenBreakfast"
  | "maintenance";

export interface CityRoleSalary {
  /** Display name for the role. */
  role: string;
  /** Department this role belongs to (drives collapsible UI grouping). */
  department: StaffDepartment;
  /** Annual minimum, native currency. */
  annualMin: number;
  /** Annual maximum, native currency. */
  annualMax: number;
  /** Optional suffix shown after the range (e.g. "+tips", "+housing"). */
  suffix?: string;
}

export interface CityStaffProfile {
  /** Display label for the city/group (e.g. "New York", "Milan / Rome"). */
  label: string;
  /** Currency symbol to render before the figure (e.g. "$", "£", "€", "¥"). */
  currencySymbol: string;
  /** ISO-ish currency code (informational). */
  currencyCode: string;
  /**
   * If true, the city is a "package" market — show the Dubai-style tooltip
   * about housing/flights/insurance being included.
   */
  packageMarket?: boolean;
  /** Ordered list of roles with native-currency annual ranges. */
  roles: CityRoleSalary[];
}

/** Department display order + labels for the UI. */
export const DEPARTMENT_ORDER: StaffDepartment[] = [
  "management",
  "frontDesk",
  "housekeeping",
  "kitchen",
  "kitchenBreakfast",
  "floor",
  "bar",
  "sommelier",
  "operations",
  "maintenance",
  "admin",
];

export const DEPARTMENT_LABELS: Record<StaffDepartment, string> = {
  kitchen: "Kitchen",
  floor: "Floor & Service",
  bar: "Bar",
  sommelier: "Sommelier & Beverage",
  operations: "Operations & Logistics",
  admin: "Admin & Tech",
  management: "Management",
  frontDesk: "Front Desk & Guest Services",
  housekeeping: "Housekeeping",
  kitchenBreakfast: "Kitchen & Breakfast",
  maintenance: "Maintenance",
};

/** Roles always considered "essential" (default count = 1). */
export const ESSENTIAL_ROLES = new Set<string>([
  // Restaurant
  "Executive Chef",
  "Sous Chef",
  "Chef de Partie",
  "Restaurant Manager",
  "Waiter / Waitress",
  "Head Bartender",
  // Virtual Kitchen
  "Head Chef",
  "Line Cook",
  "Kitchen Manager",
]);

// ─────────────────────────────────────────────────────────────────────────
// Anchor-based generator
// ─────────────────────────────────────────────────────────────────────────
//
// Each city declares two anchor compensation pairs (Sous Chef and Restaurant
// Manager) plus a few currency-specific defaults (sommelier, head bartender,
// suffixes). The generator below produces the full Restaurant role roster
// using market-tested ratios derived from the prior dataset.

interface CityAnchors {
  label: string;
  currencySymbol: string;
  currencyCode: string;
  packageMarket?: boolean;
  /** Annual Sous Chef pair, native currency. */
  sousChef: [number, number];
  /** Annual Restaurant Manager pair, native currency. */
  restMgr: [number, number];
  /** Annual Head Bartender pair, native currency. */
  headBartender: [number, number];
  /** Annual Sommelier pair, native currency. */
  sommelier: [number, number];
  /** Tip/service/housing suffix to apply to tipped roles ("+tips" by default). */
  tipsSuffix?: string;
  /** Suffix applied to all roles (used by package markets). */
  housingSuffix?: string;
}

interface RoleTemplate {
  role: string;
  department: StaffDepartment;
  /** Anchor to scale from. */
  anchor: "sousChef" | "restMgr" | "headBartender" | "sommelier";
  /** Multiplier vs. the anchor's annual range. */
  factor: number;
  /** When true, append the tip suffix (front-of-house tipped roles). */
  tipped?: boolean;
}

const ROLE_TEMPLATES: RoleTemplate[] = [
  // KITCHEN
  { role: "Executive Chef",          department: "kitchen", anchor: "sousChef", factor: 1.88 },
  { role: "Head Chef",               department: "kitchen", anchor: "sousChef", factor: 1.40 },
  { role: "Sous Chef",               department: "kitchen", anchor: "sousChef", factor: 1.00 },
  { role: "Chef de Partie",          department: "kitchen", anchor: "sousChef", factor: 0.72 },
  { role: "Demi Chef de Partie",     department: "kitchen", anchor: "sousChef", factor: 0.62 },
  { role: "Commis Chef",             department: "kitchen", anchor: "sousChef", factor: 0.54 },
  { role: "Pastry Chef",             department: "kitchen", anchor: "sousChef", factor: 1.20 },
  { role: "Pastry Sous Chef",        department: "kitchen", anchor: "sousChef", factor: 0.80 },
  { role: "Pastry Chef de Partie",   department: "kitchen", anchor: "sousChef", factor: 0.62 },
  { role: "Pastry Commis",           department: "kitchen", anchor: "sousChef", factor: 0.50 },
  { role: "Kitchen Porter",          department: "kitchen", anchor: "sousChef", factor: 0.42 },
  { role: "Steward / Dishwasher",    department: "kitchen", anchor: "sousChef", factor: 0.40 },

  // FLOOR & SERVICE
  { role: "General Manager",                  department: "floor", anchor: "restMgr", factor: 1.30 },
  { role: "Restaurant Manager",               department: "floor", anchor: "restMgr", factor: 1.00 },
  { role: "Assistant Restaurant Manager",     department: "floor", anchor: "restMgr", factor: 0.72 },
  { role: "Floor Manager / Supervisor",       department: "floor", anchor: "restMgr", factor: 0.62 },
  { role: "Maître d' / Head Waiter",          department: "floor", anchor: "restMgr", factor: 0.84 },
  { role: "Waiter / Waitress",                department: "floor", anchor: "restMgr", factor: 0.42, tipped: true },
  { role: "Demi Chef de Rang",                department: "floor", anchor: "restMgr", factor: 0.48, tipped: true },
  { role: "Commis Waiter / Runner",           department: "floor", anchor: "restMgr", factor: 0.34, tipped: true },
  { role: "Host / Hostess",                   department: "floor", anchor: "restMgr", factor: 0.38 },
  { role: "Busser",                           department: "floor", anchor: "restMgr", factor: 0.32, tipped: true },

  // BAR
  { role: "Head Bartender", department: "bar", anchor: "headBartender", factor: 1.00 },
  { role: "Bartender",      department: "bar", anchor: "headBartender", factor: 0.72, tipped: true },
  { role: "Barback",        department: "bar", anchor: "headBartender", factor: 0.50 },

  // SOMMELIER & BEVERAGE
  { role: "Head Sommelier",    department: "sommelier", anchor: "sommelier", factor: 1.30 },
  { role: "Sommelier",         department: "sommelier", anchor: "sommelier", factor: 1.00 },
  { role: "Commis Sommelier",  department: "sommelier", anchor: "sommelier", factor: 0.55 },

  // ADMIN & SUPPORT
  { role: "Operations Manager",     department: "admin", anchor: "restMgr", factor: 1.20 },
  { role: "Reservations Manager",   department: "admin", anchor: "restMgr", factor: 0.65 },
  { role: "Marketing Coordinator",  department: "admin", anchor: "restMgr", factor: 0.55 },
];

/** Round annual figure to a clean number (3 sig-figs-ish per magnitude). */
function tidy(n: number): number {
  if (n >= 1_000_000) return Math.round(n / 100_000) * 100_000;
  if (n >= 100_000) return Math.round(n / 1_000) * 1_000;
  if (n >= 10_000) return Math.round(n / 500) * 500;
  if (n >= 1_000) return Math.round(n / 100) * 100;
  return Math.round(n);
}

function buildProfile(a: CityAnchors): CityStaffProfile {
  const anchorMap = {
    sousChef: a.sousChef,
    restMgr: a.restMgr,
    headBartender: a.headBartender,
    sommelier: a.sommelier,
  } as const;

  const roles: CityRoleSalary[] = ROLE_TEMPLATES.map(t => {
    const [lo, hi] = anchorMap[t.anchor];
    const annualMin = tidy(lo * t.factor);
    const annualMax = tidy(hi * t.factor);
    let suffix: string | undefined;
    if (a.housingSuffix) suffix = a.housingSuffix;
    else if (t.tipped && a.tipsSuffix !== "") suffix = a.tipsSuffix ?? "+tips";
    return {
      role: t.role,
      department: t.department,
      annualMin,
      annualMax,
      ...(suffix ? { suffix } : {}),
    };
  });

  return {
    label: a.label,
    currencySymbol: a.currencySymbol,
    currencyCode: a.currencyCode,
    ...(a.packageMarket ? { packageMarket: true } : {}),
    roles,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Per-city anchor data (preserves prior salary levels for each market)
// ─────────────────────────────────────────────────────────────────────────

const CITY_ANCHORS: Record<string, CityAnchors> = {
  "new york": {
    label: "New York",
    currencySymbol: "$",
    currencyCode: "USD",
    sousChef: [70000, 90000],
    restMgr: [75000, 115000],
    headBartender: [55000, 75000],
    sommelier: [55000, 85000],
  },
  "miami": {
    label: "Miami",
    currencySymbol: "$",
    currencyCode: "USD",
    sousChef: [60000, 80000],
    restMgr: [65000, 95000],
    headBartender: [48000, 65000],
    sommelier: [50000, 75000],
  },
  "los angeles": {
    label: "Los Angeles",
    currencySymbol: "$",
    currencyCode: "USD",
    sousChef: [62000, 82000],
    restMgr: [68000, 100000],
    headBartender: [50000, 68000],
    sommelier: [52000, 78000],
  },
  "san francisco": {
    label: "San Francisco",
    currencySymbol: "$",
    currencyCode: "USD",
    sousChef: [68000, 88000],
    restMgr: [72000, 108000],
    headBartender: [52000, 72000],
    sommelier: [55000, 82000],
  },
  "chicago": {
    label: "Chicago",
    currencySymbol: "$",
    currencyCode: "USD",
    sousChef: [58000, 78000],
    restMgr: [62000, 90000],
    headBartender: [45000, 62000],
    sommelier: [48000, 70000],
  },
  "london": {
    label: "London",
    currencySymbol: "£",
    currencyCode: "GBP",
    sousChef: [45000, 60000],
    restMgr: [45000, 70000],
    headBartender: [38000, 55000],
    sommelier: [40000, 65000],
  },
  "paris": {
    label: "Paris",
    currencySymbol: "€",
    currencyCode: "EUR",
    sousChef: [42000, 58000],
    restMgr: [44000, 65000],
    headBartender: [35000, 50000],
    sommelier: [38000, 58000],
    tipsSuffix: "+service",
  },
  "milan": {
    label: "Milan / Rome",
    currencySymbol: "€",
    currencyCode: "EUR",
    sousChef: [35000, 50000],
    restMgr: [38000, 58000],
    headBartender: [28000, 42000],
    sommelier: [30000, 50000],
  },
  "barcelona": {
    label: "Barcelona / Madrid",
    currencySymbol: "€",
    currencyCode: "EUR",
    sousChef: [30000, 44000],
    restMgr: [34000, 52000],
    headBartender: [25000, 38000],
    sommelier: [28000, 45000],
  },
  "berlin": {
    label: "Berlin / Munich",
    currencySymbol: "€",
    currencyCode: "EUR",
    sousChef: [38000, 52000],
    restMgr: [40000, 60000],
    headBartender: [30000, 44000],
    sommelier: [32000, 50000],
    tipsSuffix: "",
  },
  "dubai": {
    label: "Dubai",
    currencySymbol: "$",
    currencyCode: "USD",
    packageMarket: true,
    sousChef: [55000, 75000],
    restMgr: [60000, 90000],
    headBartender: [42000, 60000],
    sommelier: [48000, 70000],
    housingSuffix: "+housing",
  },
  "sydney": {
    label: "Sydney / Melbourne",
    currencySymbol: "A$",
    currencyCode: "AUD",
    sousChef: [75000, 95000],
    restMgr: [75000, 105000],
    headBartender: [55000, 75000],
    sommelier: [60000, 85000],
  },
  "singapore": {
    label: "Singapore",
    currencySymbol: "S$",
    currencyCode: "SGD",
    sousChef: [55000, 78000],
    restMgr: [60000, 90000],
    headBartender: [45000, 65000],
    sommelier: [50000, 75000],
    tipsSuffix: "",
  },
  "toronto": {
    label: "Toronto / Vancouver",
    currencySymbol: "C$",
    currencyCode: "CAD",
    sousChef: [58000, 78000],
    restMgr: [60000, 88000],
    headBartender: [44000, 62000],
    sommelier: [50000, 72000],
  },
  "tokyo": {
    label: "Tokyo",
    currencySymbol: "¥",
    currencyCode: "JPY",
    sousChef: [5_500_000, 7_500_000],
    restMgr: [5_500_000, 8_000_000],
    headBartender: [4_000_000, 6_000_000],
    sommelier: [4_500_000, 7_000_000],
    tipsSuffix: "",
  },
};

/**
 * Map of city-key → profile. Keys are lowercased city names matching
 * `onboarding.city`. Multi-city groups (e.g. Milan / Rome) are registered
 * under each member key so lookups stay simple.
 */
export const CITY_STAFF_DATA: Record<string, CityStaffProfile> = Object.fromEntries(
  Object.entries(CITY_ANCHORS).map(([key, anchors]) => [key, buildProfile(anchors)]),
);

// Aliases — share a profile across multiple onboarding city keys.
CITY_STAFF_DATA["nyc"] = CITY_STAFF_DATA["new york"];
CITY_STAFF_DATA["manhattan"] = CITY_STAFF_DATA["new york"];
CITY_STAFF_DATA["brooklyn"] = CITY_STAFF_DATA["new york"];
CITY_STAFF_DATA["la"] = CITY_STAFF_DATA["los angeles"];
CITY_STAFF_DATA["sf"] = CITY_STAFF_DATA["san francisco"];
CITY_STAFF_DATA["rome"] = CITY_STAFF_DATA["milan"];
CITY_STAFF_DATA["madrid"] = CITY_STAFF_DATA["barcelona"];
CITY_STAFF_DATA["munich"] = CITY_STAFF_DATA["berlin"];
CITY_STAFF_DATA["abu dhabi"] = CITY_STAFF_DATA["dubai"];
CITY_STAFF_DATA["melbourne"] = CITY_STAFF_DATA["sydney"];
CITY_STAFF_DATA["vancouver"] = CITY_STAFF_DATA["toronto"];

/**
 * Resolve a city key to its staff profile. Performs a normalized lookup with
 * substring fallbacks so onboarding strings like "New York, NY" still match.
 * Returns `null` when no luxury data is available — the caller should fall
 * back to the generic role list.
 */
export function getCityStaffProfile(city: string): CityStaffProfile | null {
  if (!city) return null;
  const key = city.toLowerCase().trim();
  if (CITY_STAFF_DATA[key]) return CITY_STAFF_DATA[key];
  // Substring match (handles "Miami, FL", "Greater London", etc.)
  for (const [k, v] of Object.entries(CITY_STAFF_DATA)) {
    if (key.includes(k) || k.includes(key)) return v;
  }
  return null;
}

/**
 * Format an annual range as a monthly equivalent string in the city's native
 * currency, e.g. `$8,000–$12,000/mo`.
 */
export function formatMonthlyRange(role: CityRoleSalary, symbol: string): string {
  const monthlyMin = Math.round(role.annualMin / 12);
  const monthlyMax = Math.round(role.annualMax / 12);
  const fmt = (n: number) => `${symbol}${n.toLocaleString()}`;
  const suffix = role.suffix ? ` ${role.suffix}` : "";
  return `${fmt(monthlyMin)}–${fmt(monthlyMax)}/mo${suffix}`;
}
